import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { PharmacySegment } from '../../pages/admin-dashboard/pharmacy-segment/pharmacy-segment.model';
import { AuthService } from '../auth-service';

// ---------- API types ----------
interface SegmentUserApi {
  id: number;
  fullName: string;
}

interface PharmacySegmentApiItem {
  id: number;
  titleEn: string;
  titleAr: string;
  extraDiscount: number;
  paymentDeadlineInDays: number;
  createdOn: string;
  lastModifiedOn: string | null;
  createdBy: SegmentUserApi | null;
  lastModifiedBy: SegmentUserApi | null;
}

interface PharmacySegmentsApiResponse {
  total: number;
  list: PharmacySegmentApiItem[];
}

// ---------- Public types ----------
export interface GetPharmacySegmentsParams {
  pharmacySegmentTitle?: string;
  createdOnFrom?: string;
  createdOnTo?: string;
  lastModifiedOnFrom?: string;
  lastModifiedOnTo?: string;
  orderBy?: string;
  orderDir?: 'ASC' | 'DESC';
  pageNum?: number;
  pageSize?: number;
}

export interface PharmacySegmentsResult {
  total: number;
  list: PharmacySegment[];
}

// ---------- Mapper ----------
function mapApiItemToSegment(item: PharmacySegmentApiItem): PharmacySegment {
  return {
    id: item.id,
    titleEn: item.titleEn,
    titleAr: item.titleAr,
    paymentTermDays: item.paymentDeadlineInDays,
    additionalDiscount: item.extraDiscount,
    createdOn: item.createdOn,
    lastModifiedOn: item.lastModifiedOn ?? null,
    createdBy: item.createdBy ?? undefined,
    lastModifiedBy: item.lastModifiedBy ?? null,
  };
}

@Injectable({ providedIn: 'root' })
export class PharmacySegmentService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private readonly baseUrl = `${environment.pharmacyMgtApiUrl}/pharmacy/segments`;

  getSegments(params: GetPharmacySegmentsParams): Observable<PharmacySegmentsResult> {
    let httpParams = new HttpParams();

    (Object.keys(params) as (keyof GetPharmacySegmentsParams)[]).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return this.http
      .get<PharmacySegmentsApiResponse>(this.baseUrl, {
        params: httpParams,
        headers: this.getHeaders(),
      })
      .pipe(
        map((res) => ({
          total: res.total ?? 0,
          list: (res.list ?? []).map(mapApiItemToSegment),
        }))
      );
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}